import CommonTable from "components/ReactTable/Table";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Container,
  Input,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
  Table,
  FormGroup,
  CardFooter,
} from "reactstrap";
import Select from "react-select";
import NotificationAlert from "react-notification-alert";
import {
  fixedWidthCustomStyle,
  PARTS_REQ_STATUS_OPTIONS,
} from "components/Common/constants";
import { useLocation, useNavigate } from "react-router-dom";
import RightPanelCard from "components/Common/RightPanelCard";
import DatePicker from "react-datepicker";
import { getRequestSparePartsList } from "actions/sparePart";
import { useDispatch, useSelector } from "react-redux";
import { requestSparePartsList } from "constants/requestSpareParts";
import { DEFAULT_RECORDS_PER_PAGE } from "components/Common/constants";
import { getTechniciansList } from "actions/technicians";
import { getPartsRequestedListById } from "actions/requestedParts";
import { updateRequestedPartsStatus } from "actions/requestedParts";
import moment from "moment";
import { getPartsNotesByJobId } from "actions/requestedParts";
import { updatePartsNotesByJobId } from "actions/requestedParts";
import { exportPartsRequestCSV } from "actions/requestedParts";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { WORK_ORDER } from "components/Common/constants";
import { ROLES } from "components/Common/constants";
import { showAlert } from "components/Common/commonFunctions";
import { SMALL_CASE_WORK_ORDER } from "components/Common/constants";

const TAB_IDS = {
  PENDING: { value: "PENDING", label: "Pending" },
  PARTS_ON_ORDER: { value: "PART_ON_ORDER", label: "Parts on Order" },
  IN_PROGRESS: { value: "IN-PROGRESS", label: "In Progress" },
  COMPLETED: { value: "COMPLETED", label: "Completed" },
  ALL: { value: "ALL", label: "All" },
};

// ✅ Options shown in the status dropdown — includes ALL and IN-PROGRESS
const STATUS_FILTER_OPTIONS = [
  ...PARTS_REQ_STATUS_OPTIONS,
  { value: "IN-PROGRESS", label: "In Progress" },
  { value: "ALL", label: "All" },
].filter(
  // dedupe by value in case PARTS_REQ_STATUS_OPTIONS already has some of these
  (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx,
);

const mapStatusToTab = (status) => {
  const statusArray = Array.isArray(status) ? status : [status];

  const hasReady = statusArray.includes("READY_FOR_PICKUP");
  const hasPicked = statusArray.includes("PICKED_UP");
  if (hasReady && hasPicked) {
    return "IN-PROGRESS";
  }

  if (hasReady) return "READY_FOR_PICKUP";
  if (hasPicked) return "PICKED_UP";

  return statusArray[0] || statusArray;
};

function PartsRequested() {
  const location = useLocation();
  const defaultTabFromDashboard =
    location?.state?.defaultTab || TAB_IDS.PENDING.value;
  const local = localStorage.getItem("p");
  const role = JSON.parse(local)?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const [activeTab, setActiveTab] = useState(defaultTabFromDashboard);

  // Build the initial filter JSON for the default tab ONCE, outside of state,
  // so the first API call already carries the tab filter (no unfiltered call).
  const buildInitialFilterJSON = (tab) => {
    const filterParams = [];
    if (tab !== "" && tab !== "ALL") {
      let filterValues = [tab];
      let condition = "eq";
      if (tab === "IN-PROGRESS") {
        filterValues = ["READY_FOR_PICKUP", "PICKED_UP"];
        condition = "in";
      }
      filterParams.push({
        field: "PartRequestedStatus",
        condition,
        filterValues,
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };
  const initialFilterJSON = buildInitialFilterJSON(defaultTabFromDashboard);
  const [showActionsModal, setShowActionModal] = useState({
    open: false,
    data: null,
  });
  const notificationAlertRef = useRef(null);
  const [showTransitForm, setShowTransitForm] = useState(false);
  const [selectedTransitDate, setSelectedTransitDate] = useState(null);
  const [showUpdateNotes, setShowUpdateNotes] = useState({
    open: false,
    data: null,
  });

  const [filterState, setFilterState] = useState({
    jobNo: "",
    searchName: "",
    technican: "",
    activeTab: defaultTabFromDashboard,
    jobSite: "",
    filterParams: {
      filterJSON: initialFilterJSON,
      searchString: "",
    },
  });

  const requestSpareParts = useSelector(
    (state) => state.requestSpareParts?.requestSparePartsList,
  );

  const technicianListData = useSelector(
    (state) => state.technicians.techniciansList,
  );

  const [requestSparePartListState, setrequestSparePartListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [partRequestedListData, setpartRequestedListData] = useState([]);
  const [isSaveClicked, setIsSaveClicked] = useState({
    readyPickup: false,
    partsOrder: false,
    exportCsv: false,
  });

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  // =========================================================
  // ✅ buildFilterParams now accepts an optional tab override.
  //    This lets callers (Search, tab click, Clear) compute filters
  //    for the NEW tab without waiting for setState to flush.
  // =========================================================
  const buildFilterParams = (tabOverride) => {
    const tab =
      tabOverride !== undefined ? tabOverride : filterState.activeTab;
    const filterParams = [];

    // Status filter — skip on "ALL"
    if (tab !== "" && tab !== "ALL") {
      let filterValues = [tab];
      let condition = "eq";

      if (tab === "IN-PROGRESS") {
        filterValues = ["READY_FOR_PICKUP", "PICKED_UP"];
        condition = "in";
      }

      filterParams.push({
        field: "PartRequestedStatus",
        condition: condition,
        filterValues: filterValues,
      });
    }

    if (filterState.technican !== "") {
      filterParams.push({
        field: "TechnicianID",
        condition: "eq",
        filterValues: [String(filterState.technican)],
      });
    }

    if (filterState.jobSite !== "") {
      filterParams.push({
        field: "JobSite",
        condition: "eq",
        filterValues: [filterState.jobSite],
      });
    }

    if (filterState?.jobNo) {
      filterParams.push({
        field: "ID",
        condition: "eq",
        filterValues: [String(filterState?.jobNo)],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  useEffect(() => {
    getRequestSparePartsList(
      dispatch,
      pageNum,
      filterState.filterParams?.filterJSON,
      filterState.filterParams?.searchString,
    );

    return () => {
      dispatch({ type: requestSparePartsList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    getTechniciansList(dispatch);
  }, []);

  const formattedData =
    requestSpareParts?.data?.partRequest?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
        (requestSpareParts?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  useEffect(() => {
    if (requestSpareParts?.code === 200) {
      setrequestSparePartListState({
        isLoading: false,
        error: "",
        noRecords: requestSpareParts?.data?.sparePart?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (requestSpareParts?.code || requestSpareParts?.message) {
      setrequestSparePartListState({
        isLoading: false,
        error:
          requestSpareParts?.message ||
          "Failed to load spare parts list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [requestSpareParts]);



  const getRequestedPartsListById = async (data) => {
    try {
      const response = await getPartsRequestedListById(data?.data?.ID);
      if (response?.code === 200) {
        setpartRequestedListData(response.data);
      } else {
        notify("danger", "Failed to fetch requested Parts List");
      }
    } catch (error) {
      notify("danger", "Failed to fetch requested Parts List");
    }
  };

  const getNotesByJobId = async (data) => {
    try {
      const response = await getPartsNotesByJobId(data?.data);
      if (response?.code === 200) {
        setShowUpdateNotes({
          open: true,
          data: response.data,
        });
      } else {
        notify("danger", "Failed to fetch notes against this " + WORK_ORDER);
      }
    } catch (error) {
      notify("danger", "Failed to fetch notes against this " + WORK_ORDER);
    }
  };

  const updateNotesByJobId = async (data) => {
    try {
      let payload = {
        partsNote: showUpdateNotes?.data?.partsNote,
      };
      const response = await updatePartsNotesByJobId(
        showUpdateNotes?.data?.ID,
        payload,
      );
      if (response?.code === 200) {
        notify(
          "success",
          response?.message || `${WORK_ORDER} Note Updated Successfully`,
        );
        setShowUpdateNotes({
          open: false,
          data: null,
        });
      } else {
        notify("danger", "Failed to fetch notes against this " + WORK_ORDER);
      }
    } catch (error) {
      notify("danger", "Failed to fetch notes against this " + WORK_ORDER);
    }
  };

  useEffect(() => {
    if (showActionsModal?.open === true) {
      getRequestedPartsListById(showActionsModal);
    }

    if (showUpdateNotes?.open === true) {
      getNotesByJobId(showUpdateNotes);
    }
  }, [showActionsModal?.open, showUpdateNotes?.open]);

  const updatePartsReqestedStatustoPartsOnOrder = async () => {
    try {
      setIsSaveClicked((prev) => ({ ...prev, partsOrder: true }));
      let payload = {
        jobID: showActionsModal?.data?.ID,
        quotationID: partRequestedListData?.quotationID,
        status: "PART_ON_ORDER",
        etadate: moment(selectedTransitDate).format("YYYY-MM-DD"),
      };
      const res = await updateRequestedPartsStatus(payload);
      if (res?.code === 200) {
        notify("success", res?.message || "Parts Status updated sucessfully.");
        setShowActionModal({ open: false, data: null });
        const filters = buildFilterParams();
        let newParams = {
          filterJSON: filters,
          searchString: filterState.searchName,
        };

        setFilterState((prev) => ({
          ...prev,
          filterParams: newParams,
        }));
      } else {
        notify("danger", "Failed to update parts status.");
      }
    } catch (error) {
      notify("danger", "Failed to update parts status.");
    } finally {
      setShowTransitForm(false);
      setSelectedTransitDate(null);
      setIsSaveClicked((prev) => ({ ...prev, partsOrder: false }));
    }
  };

  const updatePartsReqestedStatustoReadyforPickup = async () => {
    showAlert({
      title: "Are you sure you want to mark these parts as ready for pickup?",
      message: "Please confirm that the requested parts are ready for pickup.",
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes, Mark as Ready",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setIsSaveClicked((prev) => ({ ...prev, readyPickup: true }));
          let payload = {
            jobID: showActionsModal?.data?.ID,
            quotationID: partRequestedListData?.quotationID,
            status: "READY_FOR_PICKUP",
          };
          const res = await updateRequestedPartsStatus(payload);
          if (res?.code === 200) {
            notify(
              "success",
              res?.message || "Parts Status updated sucessfully.",
            );
            setShowActionModal({ open: false, data: null });
            const filters = buildFilterParams();
            let newParams = {
              filterJSON: filters,
              searchString: filterState.searchName,
            };

            setFilterState((prev) => ({
              ...prev,
              filterParams: newParams,
            }));
          } else {
            notify("danger", "Failed to update parts status.");
          }
        } catch (error) {
          notify("danger", "Failed to update parts status.");
        } finally {
          setIsSaveClicked((prev) => ({ ...prev, readyPickup: false }));
        }
      },
      onCancel: () => { },
    });
  };

  const updateETA = async () => {
    try {
      setIsSaveClicked((prev) => ({ ...prev, partsOrder: true }));
      let payload = {
        jobID: showActionsModal?.data?.ID,
        quotationID: partRequestedListData?.quotationID,
        status: "UPDATE_ETA",
        etadate: moment(selectedTransitDate).format("YYYY-MM-DD"),
      };
      const res = await updateRequestedPartsStatus(payload);
      if (res?.code === 200) {
        notify("success", res?.message || "Parts Status updated sucessfully.");
        setShowActionModal({ open: false, data: null });
        const filters = buildFilterParams();
        let newParams = {
          filterJSON: filters,
          searchString: filterState.searchName,
        };

        setFilterState((prev) => ({
          ...prev,
          filterParams: newParams,
        }));
      } else {
        notify("danger", "Failed to update parts status.");
      }
    } catch (error) {
      notify("danger", "Failed to update parts status.");
    } finally {
      setIsSaveClicked((prev) => ({ ...prev, partsOrder: false }));
    }
  };

  const baseColumns = [
    {
      Header: `${WORK_ORDER} No.`,
      accessor: "ID",
      className: "col-sjob-no",
      centerAlign: true,
      Cell: ({ row }) =>
        role === ROLES.ADMINISTRATOR ? (
          <span
            className="cell-link"
            onClick={() => jobDetailsNavigate(row.original)}
          >
            {row.original.ID}
          </span>
        ) : (
          row.original.ID
        ),
    },
    {
      Header: "Received On",
      accessor: "partRequestedReceivedOn",
    },
    { Header: "Region", accessor: "regionResponse.name" },
    { Header: "Technician", accessor: "technician.name" },
    {
      Header: "Status",
      accessor: "partRequestedStatus",
      Cell: ({ value }) =>
        PARTS_REQ_STATUS_OPTIONS.find((item) => item.value === value)?.label ||
        value,
    },
  ];

  const jobDetailsNavigate = (rowData) => {
    const encodedId = urlEncodedId(rowData.ID);
    // ✅ Open job details in a NEW TAB
    window.open(
      `/admin/jobs/${encodedId}/details`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleEdit = (data) => {
    setShowActionModal({ open: true, data: data });
  };

  const getColumnsForTab = (activeTab) => {
    let extraColumns = [];

    if (
      [
        TAB_IDS.PARTS_ON_ORDER.value,
        TAB_IDS.IN_PROGRESS.value,
        TAB_IDS.COMPLETED.value,
      ].includes(activeTab)
    ) {
      extraColumns.push({
        Header: "Ready Pickup Done",
        accessor: "readyPickUpDone",
        Cell: ({ row }) => row.original.readyPickUpDone,
      });
    }

    if (activeTab === TAB_IDS.PARTS_ON_ORDER.value) {
      extraColumns.push(
        {
          Header: "Purchase Order No.",
          accessor: "partsPurchaseOrderNo",
          Cell: ({ row }) =>
            row.original.partsPurchaseOrderNo === ""
              ? "-"
              : row.original.partsPurchaseOrderNo,
        },
        {
          Header: "ETA Date",
          accessor: "partRequestedEtaDate",
        },
      );
    }

    return [
      ...baseColumns,
      ...extraColumns,
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ row, state }) => {
          return (
            <span
              className="link-text"
              onClick={(e) => {
                e.stopPropagation();
                setShowUpdateNotes({ open: true, data: row.original.ID });
              }}
            >
              Update Notes
            </span>
          );
        },
      },
      {
        Header: "Action",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const status = row.original.partRequestedStatus;

          const isEditable =
            status === "PENDING" || status === "PART_ON_ORDER";

          return (
            <div className="actions-right d-flex justify-content-start">
              <Button
                color="link"
                size="sm"
                className="btn-icon btn-neutral ml-1 icon-info"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row.original);
                }}
              >
                {isEditable ? (
                  <i className={`tim-icons icon-pencil tim-md`} />
                ) : (
                  <i className={`fa fa-eye`} />
                )}
              </Button>
            </div>
          );
        },
      },
    ];
  };

  const handleCloseModal = () => {
    setShowActionModal({ open: false, data: null });
    setShowTransitForm(false);
    setSelectedTransitDate(null);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateNotes({ open: false, data: null });
  };

  const handleTransitDate = (date) => {
    setSelectedTransitDate(date);
  };

  const renderActionButtons = () => {
    const status = showActionsModal?.data?.partRequestedStatus;

    switch (status) {
      case "PART_ON_ORDER":
        return (
          <Col md="12" className="action-col">
            <div className="action-buttons">
              <Button
                color="default"
                type="submit"
                className="btn-simple"
                size="sm"
                onClick={updatePartsReqestedStatustoReadyforPickup}
              >
                Ready for Pickup
              </Button>
              <Button
                color="default"
                size="sm"
                className="mr-2 btn-simple"
                onClick={() => setShowTransitForm(true)}
              >
                Update ETA
              </Button>
            </div>
          </Col>
        );

      case "PENDING":
        return (
          <Col md="12" className="action-col">
            <div className="action-buttons">
              <Button
                color="default"
                type="submit"
                className="btn-simple"
                size="sm"
                onClick={updatePartsReqestedStatustoReadyforPickup}
              >
                Ready for Pickup
              </Button>
              <Button
                color="default"
                size="sm"
                onClick={() => setShowTransitForm(true)}
                className="btn-simple"
              >
                Part on Order
              </Button>
            </div>
          </Col>
        );

      case "DELIVERED":
        return (
          <Col md="4" className="action-col">
            <div className="action-buttons">
              <Button color="success" size="sm" disabled>
                Delivered
              </Button>
            </div>
          </Col>
        );

      default:
        return <></>;
    }
  };

  // =========================================================
  // ✅ handleSearch — applies the staged status filter to the tab,
  //    builds filters for the new tab, triggers refetch.
  // =========================================================
  const handleSearch = () => {
    const pendingTab = filterState.activeTab || "ALL";

    // Build filters with the new tab directly (no setState race)
    const filterJSON = buildFilterParams(pendingTab);

    if (pendingTab === "READY_FOR_PICKUP" || pendingTab === "PICKED_UP") {
      setActiveTab(TAB_IDS.IN_PROGRESS.value,);

    } else {
      // Apply to the tab UI
      setActiveTab(pendingTab);

    }




    // Apply to the filter state
    setFilterState((prev) => ({
      ...prev,
      activeTab: pendingTab,
      filterParams: {
        filterJSON,
        searchString: prev.searchName,
      },
    }));

    setrequestSparePartListState((prev) => ({
      ...prev,
      showLoaderOnClick: true,
      isLoading: true,
    }));

    if (pageNum !== 1) setPageNum(1);
  };


const handleClear = () => {
  const resetTab = "PENDING";

  const filterParam = [
    {
      field: "PartRequestedStatus",
      condition: "eq",
      filterValues: [resetTab],   // ✅ array, not a string
    },
  ];

  setActiveTab(resetTab);          // ✅ use resetTab (was defaultTabFromDashboard)
  setFilterState({
    jobNo: "",
    searchName: "",
    technican: "",
    activeTab: resetTab,
    jobSite: "",
    filterParams: {
      filterJSON: JSON.stringify(filterParam),   // ✅ stringify
      searchString: "",
    },
  });

  setrequestSparePartListState((prev) => ({ ...prev, isLoading: true }));
  if (pageNum !== 1) setPageNum(1);
};

  const handleExportCsv = async () => {
    try {
      setIsSaveClicked((prev) => ({ ...prev, exportCsv: true }));
      const res = await exportPartsRequestCSV(filterState.filterParams);
      if (res.status === 200) {
        const blob = new Blob(["\uFEFF" + res?.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `parts-requested-report_${moment().format(
          "YYYY-MM-DD",
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify("success", res?.message || "CSV exported successfully");
      } else {
        const message = Array.isArray(res?.message || res?.data?.message)
          ? res?.message[0]?.Msg || res?.data?.message[0]?.Msg
          : res?.message ||
          res?.data?.message ||
          "Failed to export csv, try again later";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsSaveClicked((prev) => ({ ...prev, exportCsv: false }));
    }
  };

  const filterParams = filterState.filterParams || {};

  const hasSearchFilters =
    (filterState.searchName && filterState.searchName.trim() !== "") ||
    (filterState.jobNo && filterState.jobNo.trim() !== "") ||
    filterState.technican ||
    filterState.jobSite;

  const hasActiveFilters =
    hasSearchFilters || (activeTab !== "PENDING" && activeTab !== "");

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // =========================================================
  // ✅ Tab click — same pattern as Search: build filters for the new tab
  // =========================================================
  const handleTabClick = (val) => {
    const newTab = val.value;
    const filterJSON = buildFilterParams(newTab);

    setActiveTab(newTab);
    setFilterState((prev) => ({
      ...prev,
      activeTab: newTab,
      filterParams: {
        filterJSON,
        searchString: prev.searchName,
      },
    }));
    setPageNum(1);
    setrequestSparePartListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
  };

  return (
    <>
      {(isSaveClicked.readyPickup || isSaveClicked.exportCsv) && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  Parts Requested
                </CardTitle>
                <div className="d-flex align-items-center">
                  <Button
                    className="btn-simple btn-round btn-style mr-3"
                    color="default"
                    size="md"
                    onClick={handleExportCsv}
                  >
                    <i className="fas fa-arrow-up-from-bracket mr-1" /> Export
                    Parts Requested
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder={`Search by ${SMALL_CASE_WORK_ORDER} No`}
                      className="mr-2 mb-2 filter-input-small"
                      value={filterState?.jobNo}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          jobNo: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={100}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={technicianListData?.data?.technician || []}
                      placeholder="Technician"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={
                        technicianListData?.data?.technician?.find(
                          (t) => t.ID === filterState?.technican,
                        ) || ""
                      }
                      getOptionLabel={(option) => option.fullName}
                      getOptionValue={(option) => option.ID}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          technican: selected?.ID,
                        }))
                      }
                    />

                    {/* ✅ Status dropdown — only stages value, does NOT switch tab */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_FILTER_OPTIONS}
                      placeholder="Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={
                        STATUS_FILTER_OPTIONS.find(
                          (t) => t.value === filterState?.activeTab,
                        ) || ""
                      }
                      onChange={(selected) => {
                        setFilterState((prev) => ({
                          ...prev,
                          activeTab: selected?.value || "",
                        }));
                      }}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {requestSparePartListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        color="default"
                        className="mb-3 filter-btn btn-simple mt-2"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {requestSpareParts?.data?.totalRecords ? (
                    hasSearchFilters ||
                      (activeTab !== "PENDING" && activeTab !== "") ? (
                      <span className="font-weight-bold">
                        Showing {requestSpareParts?.data?.totalFilteredRecords}{" "}
                        of {requestSpareParts?.data?.totalRecords} Parts
                        Requested
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total Parts Requested:{" "}
                        {requestSpareParts?.data?.totalRecords}
                      </span>
                    )
                  ) : null}
                </div>
                <Nav className="custom-tabs mb-3">
                  {Object.entries(TAB_IDS).map(([key, val]) => (
                    <NavItem key={key}>
                      <NavLink
                        className={activeTab === val.value ? "active" : ""}
                        onClick={() => handleTabClick(val)}
                        href="#"
                      >
                        {val.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                {requestSparePartListState.isLoading ? (
                  <div className="loaderContainer text-muted">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : requestSparePartListState.error ? (
                  <div className="loaderContainer text-muted">
                    {requestSparePartListState.error}
                  </div>
                ) : requestSparePartListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No records available.
                  </div>
                ) : (
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId={activeTab}>
                      {formattedData.length > 0 ? (
                        <CommonTable
                          data={formattedData}
                          columns={getColumnsForTab(activeTab)}
                          defaultPageSize={25}
                          resizable={false}
                          showPaginationBottom={true}
                          activePage={pageNum}
                          className="-striped -highlight table-hover"
                          totalPages={Math.ceil(
                            requestSpareParts?.data?.totalFilteredRecords /
                            requestSpareParts?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE,
                          )}
                          setPageNum={setPageNum}
                        />
                      ) : (
                        <div className="loaderContainer text-muted">
                          No records available.
                        </div>
                      )}
                    </TabPane>
                  </TabContent>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <RightPanelCard
        isOpen={showActionsModal.open}
        onClose={handleCloseModal}
      >
        <Card className="mb-0 parts-requested">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0"
            tag="h4"
            toggle={handleCloseModal}
          >
            <span>
              {WORK_ORDER} No:{" "}
              <span className="text-muted">{showActionsModal?.data?.ID}</span>
            </span>
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseModal}
            />
          </CardHeader>

          <CardBody className="position-relative">
            <div className={`main-content ${showTransitForm ? "blurred" : ""}`}>
              <Row className="my-2 align-items-center">
                <Col md="4" sm="6">
                  <div>
                    <span className="mr-2 font-weight-bold">Status:</span>
                    {PARTS_REQ_STATUS_OPTIONS.find(
                      (item) => item.value === partRequestedListData?.status,
                    )?.label || "-"}
                  </div>
                </Col>

                <Col md="5" sm="6">
                  <div>
                    <span className="mr-2 font-weight-bold">Received On:</span>
                    {partRequestedListData?.requestedOn}
                  </div>
                </Col>

                {renderActionButtons()}
              </Row>

              <hr />
              <Table responsive hover className="custom-table">
                <thead>
                  <tr>
                    <th className="quotation-id-width">quotation ID</th>
                    <th className="part-number-width">Part Number</th>
                    <th className="quantity-width">Quantity</th>
                    <th className="status-custom">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {partRequestedListData?.parts?.map((item) => {
                    return (
                      <tr key={item?.id}>
                        <td className="quotation-id-width">
                          {item?.quotationID}
                        </td>
                        <td className="part-number-width">
                          {item?.itemNumber}
                        </td>
                        <td className="quantity-width">{item?.quantity}</td>
                        <td className="status-custom">
                          {PARTS_REQ_STATUS_OPTIONS.find(
                            (status) => status.value === item?.status,
                          )?.label || "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            {showTransitForm && (
              <div className="transit-overlay animate-in">
                <div className="transit-card border rounded p-3 bg-white shadow">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <div className="font-weight-bold mb-1">
                        Parts On Transit
                      </div>
                      <div className="text-muted small">
                        Please share a tentative ETA date for transit
                        completion.
                      </div>
                    </div>
                    <i
                      className="fa fa-close text-muted pointer"
                      onClick={() => {
                        setShowTransitForm(false);
                        setSelectedTransitDate(null);
                      }}
                    />
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1 mr-2">
                      <DatePicker
                        selected={selectedTransitDate || null}
                        onChange={handleTransitDate}
                        isClearable
                        placeholderText="Select a date"
                        className="form-control cust-datepicker"
                        dateFormat="dd-MM-yyyy"
                        icon={
                          <i className="fa-solid fa-calendar-day fa-md mt-1" />
                        }
                        showIcon
                        toggleCalendarOnIconClick
                        wrapperClassName="w-100"
                        popperPlacement="bottom-start"
                        popperContainer={({ children }) => (
                          <div className="fixed-popper-container">
                            {children}
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <Button
                        color="secondary"
                        onClick={() => {
                          setShowTransitForm(false);
                          setSelectedTransitDate(null);
                        }}
                        className="mr-2"
                        size="sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        color="default"
                        type="submit"
                        size="sm"
                        onClick={() => {
                          showActionsModal?.data?.partRequestedStatus ===
                            "PART_ON_ORDER"
                            ? updateETA()
                            : updatePartsReqestedStatustoPartsOnOrder();
                          setShowTransitForm(false);
                          setSelectedTransitDate(null);
                        }}
                      >
                        {isSaveClicked.partsOrder ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </RightPanelCard>

      <RightPanelCard
        isOpen={showUpdateNotes.open}
        onClose={handleCloseUpdateModal}
      >
        <Card className="mb-0">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0"
            tag="h4"
            toggle={handleCloseUpdateModal}
          >
            <span>
              Notes for {WORK_ORDER} No{" "}
              <span className="text-muted">{showUpdateNotes?.data?.ID}</span>
            </span>
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseUpdateModal}
            />
          </CardHeader>

          <CardBody>
            <Row>
              <Col md="12">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Input
                    name="notes"
                    type="textarea"
                    className="notes-textarea"
                    placeholder="Enter Notes"
                    autoComplete="off"
                    value={showUpdateNotes?.data?.partsNote}
                    onChange={(e) =>
                      setShowUpdateNotes({
                        ...showUpdateNotes,
                        data: {
                          ...showUpdateNotes.data,
                          partsNote: e.target.value,
                        },
                      })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
          <CardFooter>
            <div className="d-flex justify-content-end mb-0">
              <Button
                color="secondary"
                onClick={handleCloseUpdateModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                color="default"
                type="submit"
                onClick={updateNotesByJobId}
              >
                Save
              </Button>
            </div>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
}

export default PartsRequested;